import { pickBurstFrames as pickBurstFramesShared } from "../engine/onset.js";
export const PHASE1_CLUSTERS = ["hiss", "airy", "pop", "hum"];
export const CALIBRATION_STEPS = [
    {
        clusterId: "hiss",
        prompt: "Make a long snake hiss — ssssss",
        durationMs: 2200,
    },
    { clusterId: "airy", prompt: "Blow a soft windy fffff", durationMs: 2200 },
    { clusterId: "pop", prompt: "Pop your lips — p! p! p!", durationMs: 2500 },
    { clusterId: "hum", prompt: "Hum a steady mmmmm", durationMs: 2200 },
];
const DIMS = [
    "centroid",
    "noise",
    "voicing",
    "energy",
    "transient",
    "flatness",
    "rolloff",
    "tilt",
];
function pickBurstFrames(frames) {
    return pickBurstFramesShared(frames, {
        absoluteFloor: 0.08,
        relativeFloor: 0,
        transientWeight: 1.5,
        energyWeight: 0.5,
        voicingPenalty: 1.0,
        keepFraction: 0.4,
        minKeep: 3,
    });
}
function onsetSalience(f) {
    return f.transient * 1.4 + f.energy * 1.0 - f.voicing * 0.8;
}
function describeImpulse(frames, peakIdx) {
    const peak = frames[peakIdx];
    const peakEnergy = peak.f.energy;
    const burstCentroid = peak.f.centroid;
    let onsetIdx = peakIdx;
    for (let i = peakIdx; i > 0; i--) {
        if (frames[i].f.energy <= peakEnergy * 0.25) {
            onsetIdx = i;
            break;
        }
        onsetIdx = i - 1;
    }
    const attackMs = Math.max(0, peak.t - frames[onsetIdx].t);
    let decayTau = 0;
    const xs = [];
    const ys = [];
    for (let i = peakIdx; i < frames.length; i++) {
        const e = frames[i].f.energy;
        if (e <= peakEnergy * 0.05)
            break;
        xs.push(frames[i].t - peak.t);
        ys.push(Math.log(Math.max(e, 1e-6)));
    }
    if (xs.length >= 2) {
        const n = xs.length;
        let sx = 0, sy = 0, sxx = 0, sxy = 0;
        for (let i = 0; i < n; i++) {
            sx += xs[i];
            sy += ys[i];
            sxx += xs[i] * xs[i];
            sxy += xs[i] * ys[i];
        }
        const denom = n * sxx - sx * sx;
        if (Math.abs(denom) > 1e-9) {
            const slope = (n * sxy - sx * sy) / denom;
            if (slope < 0)
                decayTau = -1 / slope;
        }
    }
    return { attackMs, decayTau, peakEnergy, burstCentroid };
}
export function localizeImpulses(recording, expectedCount, minInterOnsetMs = 120) {
    if (recording.length === 0 || expectedCount <= 0)
        return [];
    const n = recording.length;
    const sal = new Array(n);
    for (let i = 0; i < n; i++)
        sal[i] = Math.max(0, onsetSalience(recording[i].f));
    const smooth = new Array(n);
    for (let i = 0; i < n; i++) {
        const a = sal[i - 1] ?? 0;
        const b = sal[i];
        const c = sal[i + 1] ?? 0;
        smooth[i] = (a + 2 * b + c) / 4;
    }
    let mean = 0;
    let max = 0;
    for (const v of smooth) {
        mean += v;
        if (v > max)
            max = v;
    }
    mean /= n;
    const threshold = mean + (max - mean) * 0.25;
    const candidates = [];
    for (let i = 1; i < n - 1; i++) {
        if (smooth[i] >= threshold &&
            smooth[i] >= smooth[i - 1] &&
            smooth[i] > smooth[i + 1]) {
            candidates.push({ idx: i, sal: smooth[i] });
        }
    }
    candidates.sort((a, b) => b.sal - a.sal);
    const accepted = [];
    for (const cand of candidates) {
        const tCand = recording[cand.idx].t;
        const tooClose = accepted.some((idx) => Math.abs(recording[idx].t - tCand) < minInterOnsetMs);
        if (tooClose)
            continue;
        accepted.push(cand.idx);
        if (accepted.length >= expectedCount)
            break;
    }
    accepted.sort((a, b) => recording[a].t - recording[b].t);
    const out = [];
    for (const peakIdx of accepted) {
        let refined = peakIdx;
        let bestSal = onsetSalience(recording[peakIdx].f);
        for (let d = -2; d <= 2; d++) {
            const j = peakIdx + d;
            if (j < 0 || j >= n)
                continue;
            const sv = onsetSalience(recording[j].f);
            if (sv > bestSal) {
                bestSal = sv;
                refined = j;
            }
        }
        const frames = [];
        for (let d = 0; d <= 1; d++) {
            const j = refined + d;
            if (j >= 0 && j < n)
                frames.push(recording[j].f);
        }
        out.push({
            tMs: recording[refined].t,
            frames,
            descriptor: describeImpulse(recording, refined),
        });
    }
    console.info(`[calibration] localizeImpulses expected=${expectedCount} ` +
        `candidates=${candidates.length} found=${out.length} ` +
        `(minInterOnset=${minInterOnsetMs}ms)`);
    return out;
}
export function summarize(clusterId, frames) {
    if (frames.length === 0) {
        console.warn(`[calibration] summarize '${clusterId}' got 0 frames — ` +
            `cluster center will be all zeros`);
    }
    const isSilence = clusterId === "silence";
    const isTransient = clusterId === "pop";
    const kept = isSilence
        ? frames
        : isTransient
            ? pickBurstFrames(frames)
            : frames.filter((f) => f.energy > 0.08);
    const source = isSilence || kept.length >= 5 ? kept : frames;
    console.debug(`[calibration] summarize '${clusterId}' totalFrames=${frames.length} ` +
        `kept=${kept.length} using=${source.length}`);
    if (!isSilence && kept.length < 5 && frames.length > 0) {
        console.warn(`[calibration] '${clusterId}' had few energetic frames ` +
            `(${kept.length}); falling back to all frames`);
    }
    const center = {};
    const variance = {};
    for (const k of DIMS) {
        let mean = 0;
        for (const f of source)
            mean += f[k];
        mean /= Math.max(1, source.length);
        let v = 0;
        for (const f of source)
            v += (f[k] - mean) ** 2;
        v = Math.sqrt(v / Math.max(1, source.length));
        center[k] = mean;
        variance[k] = Math.max(0.08, v);
    }
    console.info(`[calibration] '${clusterId}' center=` +
        JSON.stringify(Object.fromEntries(DIMS.map((k) => [k, +center[k].toFixed(3)]))));
    return { id: clusterId, center, variance };
}
export function collectStep(stream, step) {
    console.info(`[calibration] collectStep '${step.clusterId}' ` +
        `durationMs=${step.durationMs}`);
    return new Promise((resolve) => {
        const frames = [];
        const start = performance.now();
        const tick = () => {
            const events = stream.drain();
            for (const e of events)
                frames.push(e.features);
            if (performance.now() - start >= step.durationMs) {
                console.debug(`[calibration] step '${step.clusterId}' collected ` +
                    `${frames.length} frames`);
                resolve(summarize(step.clusterId, frames));
            }
            else {
                requestAnimationFrame(tick);
            }
        };
        requestAnimationFrame(tick);
    });
}
